/**
 * Per-desktop local cache (localStorage) — "Meus Desktops"
 *
 * Every loaded desktop keeps a live local copy on disk so switching desktops
 * never discards client changes. Each entry also carries the hash of the
 * last server-synced state (baseline), which is what makes a desktop "dirty":
 *   dirty = hash(sanitized snapshot) !== baselineHash
 *
 * Entry shape (JSON, one localStorage key per desktop):
 *   { snapshot, baselineHash, timestamp }
 */

import logger from "../../utils/logger";
import { getDesktopCacheKey, getDesktopCachePrefix } from "../constants.js";
import { createWorkspaceSnapshot } from "../core/serialization.js";

/**
 * FlexLayout rewrites these on every mount without user action (tabset ids
 * regenerate, active/selected follow focus, popout window rects get
 * OS-clamped/grid-snapped on reopen) — if hashed, every freshly loaded
 * desktop would flag dirty. They are still SAVED in the snapshot; they just
 * don't count as an "unsaved change". `weight` (splitter position) IS hashed:
 * the snapshot reads the live model post-normalization, so it only changes
 * when the user actually drags a splitter.
 */
const DOCK_VOLATILE_KEYS = ["id", "active", "selected", "rect"];

const sanitizeDockNode = (node) => {
  if (Array.isArray(node)) return node.map(sanitizeDockNode);
  if (!node || typeof node !== "object") return node;
  const rest = {};
  for (const [key, value] of Object.entries(node)) {
    if (DOCK_VOLATILE_KEYS.includes(key)) continue;
    rest[key] = sanitizeDockNode(value);
  }
  return rest;
};

/**
 * Fields that change without user action and must not count as "unsaved
 * changes": serialization noise (timestamp/metadata), viewport state
 * (xExtents/visibleBars), desktopName (server-side rename metadata),
 * orderDefaultsData (auto-seeded per active symbol) and the volatile parts
 * of the FlexLayout dock tree.
 */
const sanitizeForHash = (snapshot) => {
  if (!snapshot || typeof snapshot !== "object") return snapshot;
  const rest = { ...snapshot };
  delete rest.timestamp;
  delete rest.metadata;
  delete rest.desktopName;
  delete rest.orderDefaultsData;
  if (rest.workspaceLayout?.dockLayout) {
    rest.workspaceLayout = {
      ...rest.workspaceLayout,
      dockLayout: sanitizeDockNode(rest.workspaceLayout.dockLayout),
    };
  }
  rest.charts = (snapshot.charts || []).map((chart) => {
    const chartRest = { ...chart };
    delete chartRest.xExtents;
    delete chartRest.xExtentsLastIndex;
    delete chartRest.visibleBars;
    return chartRest;
  });
  return rest;
};

/**
 * FNV-1a 32-bit hash — small, fast, deterministic. Collision risk is
 * irrelevant here (worst case: a dirty desktop shows as clean once).
 * @param {Object} snapshot - Workspace snapshot
 * @returns {string} - Hash of the sanitized snapshot
 */
export const computeSnapshotHash = (snapshot) => {
  const str = JSON.stringify(sanitizeForHash(snapshot));
  let hash = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193);
  }
  return (hash >>> 0).toString(36);
};

/**
 * Read a cache entry for a desktop
 * @returns {Object|null} - { snapshot, baselineHash, timestamp } or null
 */
export const getDesktopCache = (user, broker, desktopId) => {
  if (desktopId == null) return null;
  try {
    const raw = localStorage.getItem(
      getDesktopCacheKey(user, broker, desktopId),
    );
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry?.snapshot?.charts) return null;
    return entry;
  } catch (error) {
    console.error("[DesktopCache] Failed to read cache:", error);
    return null;
  }
};

/**
 * List all cached desktops for a user/broker
 * @returns {Object} - { [desktopId]: { dirty, timestamp } }
 */
export const listDesktopCacheStates = (user, broker) => {
  const prefix = getDesktopCachePrefix(user, broker);
  const states = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(prefix)) continue;
      const desktopId = key.slice(prefix.length);
      try {
        const entry = JSON.parse(localStorage.getItem(key));
        if (!entry?.snapshot) continue;
        states[desktopId] = {
          dirty: isDesktopCacheDirty(entry),
          timestamp: entry.timestamp,
        };
      } catch {
        // Corrupt entry: drop it (safe — server copy still exists)
        localStorage.removeItem(key);
        i--;
      }
    }
  } catch (error) {
    console.error("[DesktopCache] Failed to list cache states:", error);
  }
  return states;
};

/**
 * @param {Object} entry - Cache entry
 * @returns {boolean} - True if the cached snapshot differs from the baseline
 */
export const isDesktopCacheDirty = (entry) => {
  if (!entry?.snapshot || !entry.baselineHash) return false;
  return computeSnapshotHash(entry.snapshot) !== entry.baselineHash;
};

/**
 * Write an entry, evicting other cached desktops on quota exhaustion.
 * Eviction policy: clean entries first (re-fetchable from server),
 * then oldest dirty entries — never the entry being written.
 * @returns {boolean} - Success
 */
const writeDesktopCache = (user, broker, desktopId, entry) => {
  const key = getDesktopCacheKey(user, broker, desktopId);
  const value = JSON.stringify(entry);
  const prefix = getDesktopCachePrefix(user, broker);

  for (;;) {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      // Quota exceeded → evict one sibling entry and retry
      const siblings = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k || !k.startsWith(prefix) || k === key) continue;
        try {
          const e = JSON.parse(localStorage.getItem(k));
          siblings.push({
            key: k,
            dirty: isDesktopCacheDirty(e),
            timestamp: e?.timestamp || "",
          });
        } catch {
          siblings.push({ key: k, dirty: false, timestamp: "" });
        }
      }
      if (siblings.length === 0) {
        console.error(
          "[DesktopCache] Quota exceeded, nothing left to evict:",
          error,
        );
        logger.workspace.error(
          "Sem espaço para salvar a cópia local do desktop",
          { desktopId },
        );
        return false;
      }
      // Clean before dirty, oldest first within each group
      siblings.sort(
        (a, b) =>
          (a.dirty ? 1 : 0) - (b.dirty ? 1 : 0) ||
          a.timestamp.localeCompare(b.timestamp),
      );
      const victim = siblings[0];
      localStorage.removeItem(victim.key);
      logger.workspace.warning(
        "Espaço local cheio — cópia local de outro desktop foi descartada",
        { evictedKey: victim.key, wasDirty: victim.dirty },
      );
    }
  }
};

/**
 * Snapshot the current workspace into the desktop's cache entry.
 * Preserves the existing baseline; when none exists (first save for this
 * desktop) the entry starts clean against itself.
 * @returns {boolean} - Success
 */
export const saveDesktopToCache = (chartsStore, user, broker, desktopId) => {
  if (desktopId == null) return false;
  try {
    const { charts } = chartsStore;
    if (!charts || charts.length === 0) return false;

    const snapshot = createWorkspaceSnapshot(chartsStore, {
      // Full serialization — same shape as server saves: strips DEFAULT
      // drawing appearances, which materialize lazily in the store when a
      // panel mounts and would otherwise read as a spurious dirty
      simplified: false,
    });
    const existing = getDesktopCache(user, broker, desktopId);
    const entry = {
      snapshot,
      baselineHash: existing?.baselineHash || computeSnapshotHash(snapshot),
      timestamp: new Date().toISOString(),
    };
    return writeDesktopCache(user, broker, desktopId, entry);
  } catch (error) {
    console.error("[DesktopCache] Failed to save cache:", error);
    return false;
  }
};

/**
 * Re-capture the baseline from the CURRENT store state — call after the
 * desktop is loaded from / saved to the server, once restoration settled
 * (comparison symbols land ~500ms after load; see deserializeWorkspace).
 * Marks the desktop clean.
 * @returns {boolean} - Success
 */
export const captureDesktopBaseline = (
  chartsStore,
  user,
  broker,
  desktopId,
) => {
  if (desktopId == null) return false;
  try {
    const snapshot = createWorkspaceSnapshot(chartsStore, {
      // Full serialization — same shape as server saves: strips DEFAULT
      // drawing appearances, which materialize lazily in the store when a
      // panel mounts and would otherwise read as a spurious dirty
      simplified: false,
    });
    const entry = {
      snapshot,
      baselineHash: computeSnapshotHash(snapshot),
      timestamp: new Date().toISOString(),
    };
    if (typeof window !== "undefined") {
      window.__LAST_BASELINE__ = {
        desktopId,
        json: JSON.stringify(sanitizeForHash(snapshot)),
      };
    }
    return writeDesktopCache(user, broker, desktopId, entry);
  } catch (error) {
    console.error("[DesktopCache] Failed to capture baseline:", error);
    return false;
  }
};

/**
 * Discard the local copy of a desktop ("Recarregar do servidor")
 */
export const clearDesktopCache = (user, broker, desktopId) => {
  if (desktopId == null) return;
  try {
    localStorage.removeItem(getDesktopCacheKey(user, broker, desktopId));
  } catch (error) {
    console.error("[DesktopCache] Failed to clear cache:", error);
  }
};

/**
 * Live dirty check for the ACTIVE desktop: current store state vs the
 * stored baseline (the cache entry may lag behind the store).
 * @returns {boolean}
 */
// Dev/E2E debug handles (mirrors the window.__*__ pattern used elsewhere)
if (typeof window !== "undefined") {
  window.__DESKTOP_CACHE_DEBUG__ = {
    sanitizeForHash,
    computeSnapshotHash,
    createWorkspaceSnapshot,
  };
}

export const isCurrentStateDirty = (chartsStore, user, broker, desktopId) => {
  if (desktopId == null) return false;
  const entry = getDesktopCache(user, broker, desktopId);
  if (!entry?.baselineHash) return false;
  try {
    const snapshot = createWorkspaceSnapshot(chartsStore, {
      // Full serialization — same shape as server saves: strips DEFAULT
      // drawing appearances, which materialize lazily in the store when a
      // panel mounts and would otherwise read as a spurious dirty
      simplified: false,
    });
    return computeSnapshotHash(snapshot) !== entry.baselineHash;
  } catch (error) {
    console.error("[DesktopCache] Failed live dirty check:", error);
    return false;
  }
};
